import { ChangeEvent, FC, useEffect } from 'react';

import { capitalizeFirstLetter } from '@eneco/common/string';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { InputText, RadioButton, RadioGroup } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SolarBoilerLeadFlowPersonalDetailsStepRendering } from '@sitecore/types/SolarBoilerLeadFlowPersonalDetailsStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { SolarBoilerLeadFlowContext } from '../../types/context';

interface FormFields {
  salutation: string;
  firstName: string;
  surnamePreposition: string;
  surname: string;
}

export const PersonalDetailsStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<SolarBoilerLeadFlowContext>();

  const sendToFlowMachine = useFlowActorRef().send;
  const salutation = useFlowSelector(state => state.context.salutation);
  const firstName = useFlowSelector(state => state.context.firstName);
  const surname = useFlowSelector(state => state.context.surname);
  const surnamePreposition = useFlowSelector(state => state.context.surnamePreposition);

  const SURNAME_PREPOSITION_MAX_LENGTH = 9; // imposed by upstream service
  const { SolarBoilerLeadFlowPersonalDetailsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SolarBoilerLeadFlowPersonalDetailsStep: SolarBoilerLeadFlowPersonalDetailsStepRendering;
  }>();

  const formSchema = yup.object({
    salutation: yup.string().required(textualData?.personalDetails?.salutationList?.value?.requiredMessage),
    firstName: yup.string().required(textualData?.personalDetails?.firstNameFormField?.value.requiredMessage),
    surnamePreposition: yup.string().max(SURNAME_PREPOSITION_MAX_LENGTH),
    surname: yup.string().required(textualData?.personalDetails?.surnameFormField?.value.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    register,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onTouched',
    defaultValues: {
      salutation,
      firstName,
      surname,
      surnamePreposition,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          salutation: formData.salutation,
          firstName: formData.firstName,
          surnamePreposition: formData.surnamePreposition || undefined,
          surname: formData.surname,
        },
      });
    }
  }, [isSubmitSuccessful, sendToFlowMachine, getValues]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
      </Header>

      <Controller
        control={control}
        name="salutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            label={textualData?.personalDetails?.salutationList?.value?.label ?? ''}
            name={name}
            direction="row"
            value={value}
            onValueChange={onChange}
            error={errors.salutation?.message}>
            {textualData?.personalDetails?.salutationList?.value?.options?.map(option => (
              <RadioButton value={option.name} key={option.name}>
                {option.label}
              </RadioButton>
            ))}
          </RadioGroup>
        )}
      />

      <InputText
        {...register('firstName', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('firstName', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        label={textualData?.personalDetails?.firstNameFormField?.value?.label ?? ''}
        placeholder={textualData?.personalDetails?.firstNameFormField?.value?.placeholder}
        hint={textualData?.personalDetails?.firstNameFormField?.value?.hint}
        error={errors.firstName?.message}
      />

      <InputText
        {...register('surnamePreposition')}
        label={textualData?.personalDetails?.surnamePrepositionFormField?.value?.label ?? ''}
        placeholder={textualData?.personalDetails?.surnamePrepositionFormField?.value?.placeholder}
        hint={textualData?.personalDetails?.surnamePrepositionFormField?.value?.hint}
        error={errors.surnamePreposition?.message}
      />

      <InputText
        {...register('surname', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('surname', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        label={textualData?.personalDetails?.surnameFormField?.value?.label ?? ''}
        placeholder={textualData?.personalDetails?.surnameFormField?.value?.placeholder}
        hint={textualData?.personalDetails?.surnameFormField?.value?.hint}
        error={errors.surname?.message}
      />

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
